import React, {useEffect, useState} from 'react'
import Drawer from 'react-modern-drawer'
import 'react-modern-drawer/dist/index.css'
import {addDoc, collection, doc, getDoc, serverTimestamp, setDoc} from 'firebase/firestore'
import {useAuth} from '../../../../modules/auth'
import db from '../../../../../services/firebase'
import {ToastContainer} from 'react-toastify'
import DrawerHeader from 'app/components/Drawer/DrawerHeader'
import DrawerFooter from 'app/components/Drawer/DrawerFooter'
import {Form, Row, Col, Card} from 'react-bootstrap'
import Input from 'app/components/Input'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Textarea from 'app/components/Textarea'
import './SwitchCustom.css'
import {toAbsoluteUrl} from '_metronic/helpers'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'

export interface addAsset {
  msg: string
  message: string
  checked?: boolean | undefined
}
interface AddAssetsProps {
  isOpen: boolean
  onClose: () => void
}

interface CookieFormValues {
  widgetMaxWidth: number
  placement: string
  title: string
  description: string
  allowAllLabel: string
  denyAllLabel: string
  customizeLabel: string
  windowMaxWidth: number
  companyName: string
  disclaimerText: string
  instructionText: string
  savePreferencesLabel: string
  necessaryCookiesTitle: string
  necessaryCookiesDescription: string
  performanceCookiesEnabled: boolean
  performanceCookiesTitle: string
  performanceCookiesDescription: string
  targetingCookiesEnabled: boolean
  targetingCookiesTitle: string
  targetingCookiesDescription: string
  functionalCookiesEnabled: boolean
  functionalCookiesTitle: string
  functionalCookiesDescription: string
}

const initialValues: CookieFormValues = {
  widgetMaxWidth: 335,
  placement: 'bottom-left',
  title: 'We Value Your Privacy 🍪',
  description:
    "This website or its third-party tools process personal data. You can choose to provide specific permissions by clicking on “Customize” instead of opting for the default settings.",
  allowAllLabel: 'Allow All',
  denyAllLabel: 'Deny All',
  customizeLabel: 'Customize',
  windowMaxWidth: 600,
  companyName: 'SecureSlate',
  disclaimerText:
    'We use cookie to store information about how you use our website, such as the pages you visit. These are the types of cookies:',
  instructionText: 'Choose Which Cookies To Enable',
  savePreferencesLabel: 'Save Preferences',
  necessaryCookiesTitle: 'Necessary Cookies',
  necessaryCookiesDescription:
    "These cookies are essential for website functions like logging in or setting privacy preferences. You can block them, but some features may not work. They don't store personal data.",
  performanceCookiesEnabled: true,
  performanceCookiesTitle: 'Performance',
  performanceCookiesDescription:
    'These cookies help track site visits and performance anonymously, allowing us to improve the site. Without them, we cannot monitor site activity or performance.',
  targetingCookiesEnabled: true,
  targetingCookiesTitle: 'Targeting',
  targetingCookiesDescription:
    "Set by advertising partners, these cookies create a profile of your interests for targeted ads. They don't store personal data, but use your device ID. Blocking them results in less targeted advertising.",
  functionalCookiesEnabled: true,
  functionalCookiesTitle: 'Functional',
  functionalCookiesDescription:
    'These cookies help track site visits and performance anonymously, allowing us to improve the site. Without them, we cannot monitor site activity or performance.',
}

const validationSchema = Yup.object().shape({
  widgetMaxWidth: Yup.number()
    .required('Widget max width is required')
    .min(200, 'Minimum width is 200px')
    .max(800, 'Maximum width is 800px'),
  placement: Yup.string()
    .required('Placement is required')
    .oneOf(['bottom-left', 'bottom-right'], 'Invalid placement'),
  title: Yup.string().required('Title is required').max(100, 'Title is too long'),
  description: Yup.string().required('Description is required').max(500, 'Description is too long'),
  allowAllLabel: Yup.string().required('Allow All label is required'),
  denyAllLabel: Yup.string().required('Deny All label is required'),
  customizeLabel: Yup.string().required('Customize label is required'),
  windowMaxWidth: Yup.number()
    .required('Window max width is required')
    .min(400, 'Minimum width is 400px')
    .max(1200, 'Maximum width is 1200px'),
  companyName: Yup.string().required('Company name is required'),
  disclaimerText: Yup.string().required('Disclaimer text is required'),
  instructionText: Yup.string().required('Instruction text is required'),
  savePreferencesLabel: Yup.string().required('Save preferences label is required')
  .matches(/^[^0-9]*$/, 'Save preferences label cannot contain numbers'),
  performanceCookiesTitle: Yup.string()
    .required('Performance cookies title is required')
    .max(100, 'Title is too long'),
  performanceCookiesDescription: Yup.string()
    .required('Performance cookies description is required')
    .max(500, 'Description is too long'),
  targetingCookiesTitle: Yup.string()
    .required('Targeting cookies title is required')
    .max(100, 'Title is too long'),
  targetingCookiesDescription: Yup.string()
    .required('Targeting cookies description is required')
    .max(500, 'Description is too long'),
  functionalCookiesTitle: Yup.string()
    .required('Functional cookies title is required')
    .max(100, 'Title is too long'),
  functionalCookiesDescription: Yup.string()
    .required('Functional cookies description is required')
    .max(500, 'Description is too long'),
})

const CookieCustomizationDrawer: React.FC<AddAssetsProps> = ({isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const {currentUser} = useAuth()
  const [initialFormValues, setInitialFormValues] = useState<CookieFormValues>(initialValues)

  useEffect(() => {
    const fetchCookieSettings = async () => {
      if (currentUser && isOpen) {
        try {
          const tenantRef = doc(db, 'tenants', currentUser.tenantId!)
          const cookieRef = doc(tenantRef, 'settings', 'cookie')
          const cookieDoc = await getDoc(cookieRef)

          if (cookieDoc.exists()) {
            const data = cookieDoc.data()
            setInitialFormValues({
              widgetMaxWidth: data.widgetMaxWidth || initialValues.widgetMaxWidth,
              placement: data.placement || initialValues.placement,
              title: data.title || initialValues.title,
              description: data.description || initialValues.description,
              allowAllLabel: data.allowAllLabel || initialValues.allowAllLabel,
              denyAllLabel: data.denyAllLabel || initialValues.denyAllLabel,
              customizeLabel: data.customizeLabel || initialValues.customizeLabel,
              windowMaxWidth: data.windowMaxWidth || initialValues.windowMaxWidth,
              companyName: data.companyName || initialValues.companyName,
              disclaimerText: data.disclaimerText || initialValues.disclaimerText,
              instructionText: data.instructionText || initialValues.instructionText,
              savePreferencesLabel: data.savePreferencesLabel || initialValues.savePreferencesLabel,
              necessaryCookiesTitle: data.necessaryCookiesTitle || initialValues.necessaryCookiesTitle,
              necessaryCookiesDescription: data.necessaryCookiesDescription || initialValues.necessaryCookiesDescription,
              performanceCookiesEnabled: data.performanceCookiesEnabled ?? initialValues.performanceCookiesEnabled,
              performanceCookiesTitle: data.performanceCookiesTitle || initialValues.performanceCookiesTitle,
              performanceCookiesDescription: data.performanceCookiesDescription || initialValues.performanceCookiesDescription,
              targetingCookiesEnabled: data.targetingCookiesEnabled ?? initialValues.targetingCookiesEnabled,
              targetingCookiesTitle: data.targetingCookiesTitle || initialValues.targetingCookiesTitle,
              targetingCookiesDescription: data.targetingCookiesDescription || initialValues.targetingCookiesDescription,
              functionalCookiesEnabled: data.functionalCookiesEnabled ?? initialValues.functionalCookiesEnabled,
              functionalCookiesTitle: data.functionalCookiesTitle || initialValues.functionalCookiesTitle,
              functionalCookiesDescription: data.functionalCookiesDescription || initialValues.functionalCookiesDescription,
            })
          }
        } catch (error) {
          console.error('Error fetching cookie settings:', error)
          showErrorToast({message: 'Failed to fetch cookie settings'})
        }
      }
    }

    fetchCookieSettings()
  }, [currentUser, isOpen])


  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true)
      try {
        if (currentUser) {
          const tenantRef = doc(db, 'tenants', currentUser.tenantId!)
          const cookieRef = doc(tenantRef, 'settings', 'cookie')

          const escapedValues = {
            ...values,
            title: values.title.replace(/'/g, '&apos;'),
            description: values.description.replace(/'/g, '&apos;'),
            disclaimerText: values.disclaimerText.replace(/'/g, '&apos;'),
            instructionText: values.instructionText.replace(/'/g, '&apos;'),
            necessaryCookiesDescription: values.necessaryCookiesDescription.replace(/'/g, '&apos;'),
            performanceCookiesDescription: values.performanceCookiesDescription.replace(
              /'/g,
              '&apos;'
            ),
            targetingCookiesDescription: values.targetingCookiesDescription.replace(/'/g, '&apos;'),
            functionalCookiesDescription: values.functionalCookiesDescription.replace(
              /'/g,
              '&apos;'
            ),
          }

          const cookieWidgetConfig = {
            companyName: escapedValues.companyName,
            cookieDuration: 365,
            modalTitle: escapedValues.title,
            modalDescription: escapedValues.description,
            theme: {
              maxWidth: `${values.widgetMaxWidth}px`,
              modalMaxWidth: `${values.windowMaxWidth}px`,
            },
            cookieWindow: {
              modalPreferenceCenterText: `Cookies on ${values.companyName}`,
              disclaimerText: escapedValues.disclaimerText,
              manageConsentPreferencesText: escapedValues.instructionText,
              confirmMyChoicesText: escapedValues.savePreferencesLabel,
              closeModalText: 'Cancel',
              cookiesTypeStrictlyNecessary: escapedValues.necessaryCookiesTitle,
              cookiesTypeStrictlyNecessaryText: escapedValues.necessaryCookiesDescription,
              cookiesTypePerformance: values.performanceCookiesEnabled ? escapedValues.performanceCookiesTitle : null,
              cookiesTypePerformanceText: values.performanceCookiesEnabled ? escapedValues.performanceCookiesDescription : null,
              cookiesTypeTargeting: values.targetingCookiesEnabled ? escapedValues.targetingCookiesTitle : null,
              cookiesTypeTargetingText: values.targetingCookiesEnabled ? escapedValues.targetingCookiesDescription : null,
              cookiesTypeFunctional: values.functionalCookiesEnabled ? escapedValues.functionalCookiesTitle : null,
              cookiesTypeFunctionalText: values.functionalCookiesEnabled ? escapedValues.functionalCookiesDescription : null,
            },
            cookieWidget: {
              placement: values.placement === 'bottom-left' ? 'bl' : 'br',
              allowAllText: escapedValues.allowAllLabel,
              manageSettingsText: escapedValues.customizeLabel,
              removeAllText: escapedValues.denyAllLabel,
            },
          }

          await setDoc(cookieRef, {
            ...values,
            cookieWidgetConfig,
            updatedAt: serverTimestamp(),
          })

          const currentDate = new Date()
          const todaysDate = `${
            currentDate.getMonth() + 1
          }/${currentDate.getDate()}/${currentDate.getFullYear()}`

          const activitiesRef = collection(tenantRef, 'activities')
          await addDoc(activitiesRef, {
            message: 'Cookie preferences updated',
            timestamp: serverTimestamp(),
            tenantId: currentUser.tenantId,
            uid: currentUser.uid,
            username: currentUser.userName,
            date: todaysDate,
            action: 'Settings updated',
            target: 'Cookie Settings',
            email: currentUser.email,
          })

          showSuccessToast({message: 'Cookie preferences updated successfully'})
          onClose()
        }
      } catch (error) {
        console.error('Error updating cookie preferences:', error)
        showErrorToast({message: 'Failed to update cookie preferences'})
      } finally {
        setIsLoading(false)
      }
    },
  })

  const handleClose = async () => {
    onClose()
    return Promise.resolve()
  }

  const handleConnectSubmit = async () => {
    return formik.handleSubmit()
  }

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} direction='right' size='45%'>
        <DrawerHeader title='Customize Cookie Widget & Window' onClose={onClose} />
        <div
          className=' scroll-y overflow-auto mb-4'
          tabIndex={-1}
          style={{height: 'calc(100vh - 120px)', padding: '20px'}}
        >
          <div>
            <h6 className='fw-bold text-gray-900' style={{marginBottom: '20px'}}>
              Cookie Widget
            </h6>
            <div style={{marginBottom: '20px'}}>
              <Input
                type='number'
                label='Cookie Widget Max Width (px)'
                {...formik.getFieldProps('widgetMaxWidth')}
                error={
                  formik.touched.widgetMaxWidth && formik.errors.widgetMaxWidth
                    ? formik.errors.widgetMaxWidth
                    : ''
                }
              />
            </div>

            <div style={{marginBottom: '20px'}}>
              <Input
                type='text'
                label='Title'
                {...formik.getFieldProps('title')}
                error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
              />
            </div>

            <div style={{marginBottom: '20px'}}>
              <Form.Label className='fw-semibold text-gray-900' style={{marginBottom: '8px'}}>
                Placement
              </Form.Label>
              <div role='radio' className='d-flex align-items-center' style={{gap: '12px'}}>
                {[
                  {
                    title: 'Bottom Left',
                    value: 'bottom-left',
                    icon: '/media/icon/arrow-down-left.svg',
                  },
                  {
                    title: 'Bottom Right',
                    value: 'bottom-right',
                    icon: '/media/icon/arrow-down-right.svg',
                  },
                ].map((option, idx) => (
                  <div
                    key={idx}
                    className='flex-fill cursor-pointer'
                    style={{
                      backgroundColor: `${
                        formik.values.placement === option.value ? '#F9FAFB' : 'white'
                      }`,
                      border: `${
                        formik.values.placement === option.value
                          ? '2px solid black'
                          : '2px solid #E5E5E5'
                      }`,
                      borderRadius: '8px',
                      padding: '8px 16px',
                    }}
                    onClick={() => {
                      formik.setFieldValue('placement', option.value)
                    }}
                  >
                    <div
                      className='d-flex justify-content-center align-items-center'
                      style={{gap: '6px'}}
                    >
                      <img
                        alt={'icon'}
                        src={toAbsoluteUrl(option.icon)}
                        className=''
                        style={{height: '16px', width: '16px'}}
                      />
                      <span className='text-gray-900 lh-base' style={{fontSize: '14px'}}>
                        {option.title}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{marginBottom: '20px'}}>
              <Textarea
                label='Description'
                error={
                  formik.touched.description && formik.errors.description
                    ? formik.errors.description
                    : ''
                }
                {...formik.getFieldProps('description')}
              />
            </div>

            <div style={{marginBottom: '20px'}}>
              <Form.Label className='fw-semibold text-gray-900' style={{marginBottom: '16px'}}>
                Button Label
              </Form.Label>
              <div className='d-flex align-items-center' style={{marginBottom: '12px'}}>
                <div className='col-4'>
                  <span className='fw-semibold text-gray-900'>Allow All</span>
                </div>
                <div className='col-8'>
                  <Input
                    type='text'
                    defaultValue='Allow All'
                    {...formik.getFieldProps('allowAllLabel')}
                    error={
                      formik.touched.allowAllLabel && formik.errors.allowAllLabel
                        ? formik.errors.allowAllLabel
                        : ''
                    }
                  />
                </div>
              </div>
              <div className='d-flex align-items-center' style={{marginBottom: '12px'}}>
                <div className='col-4'>
                  <span className='fw-semibold text-gray-900'>Deny All</span>
                </div>
                <div className='col-8'>
                  <Input
                    type='text'
                    defaultValue='Deny All'
                    {...formik.getFieldProps('denyAllLabel')}
                    error={
                      formik.touched.denyAllLabel && formik.errors.denyAllLabel
                        ? formik.errors.denyAllLabel
                        : ''
                    }
                  />
                </div>
              </div>
              <div className='d-flex align-items-center'>
                <div className='col-4'>
                  <span className='fw-semibold text-gray-900'>Customize</span>
                </div>
                <div className='col-8'>
                  <Input
                    type='text'
                    defaultValue='Customize'
                    {...formik.getFieldProps('customizeLabel')}
                    error={
                      formik.touched.customizeLabel && formik.errors.customizeLabel
                        ? formik.errors.customizeLabel
                        : ''
                    }
                  />
                </div>
              </div>
            </div>

            <div className='separator' style={{marginBottom: '24px'}}></div>

            <h6 className='fw-bold' style={{marginBottom: '16px'}}>
              Cookie Window
            </h6>

            <div style={{marginBottom: '20px'}}>
              <Input
                type='number'
                label='Cookie Window Max Width (px)'
                {...formik.getFieldProps('windowMaxWidth')}
                error={
                  formik.touched.windowMaxWidth && formik.errors.windowMaxWidth
                    ? formik.errors.windowMaxWidth
                    : ''
                }
              />
            </div>

            <div style={{marginBottom: '20px'}}>
              <Input
                type='text'
                label='Company Name'
                {...formik.getFieldProps('companyName')}
                error={
                  formik.touched.companyName && formik.errors.companyName
                    ? formik.errors.companyName
                    : ''
                }
              />
            </div>

            <div style={{marginBottom: '20px'}}>
              <Textarea
                label='Disclaimer Text'
                error={
                  formik.touched.disclaimerText && formik.errors.disclaimerText
                    ? formik.errors.disclaimerText
                    : ''
                }
                {...formik.getFieldProps('disclaimerText')}
              />
            </div>

            <div style={{marginBottom: '20px'}}>
              <Input
                type='text'
                label='Instruction Text'
                {...formik.getFieldProps('instructionText')}
                error={
                  formik.touched.instructionText && formik.errors.instructionText
                    ? formik.errors.instructionText
                    : ''
                }
              />
            </div>

            <Form.Group style={{marginBottom: '20px'}}>
              <Form.Label className=' fw-semibold text-gray-900' style={{marginBottom: '16px'}}>
                Cookie Options
              </Form.Label>

              {/* Necessary Cookies */}
              <Card className=' border-0 bg-gray-200' style={{marginBottom: '16px'}}>
                <Card.Body style={{padding: '20px'}}>
                  <div className='fw-bold' style={{marginBottom: '16px'}}>
                    Necessary Cookies
                  </div>
                  <div className='fs-7 fw-semibold text-gray-900' style={{marginBottom: '16px'}}>
                    * Users will always have to select necessary cookies from their cookie
                    preferences
                  </div>
                  <div style={{marginBottom: '16px'}}>
                    <Input
                      type='text'
                      placeholder='Necesaary'
                      autoComplete='off'
                      error={
                        formik.touched.necessaryCookiesTitle && formik.errors.necessaryCookiesTitle
                          ? formik.errors.necessaryCookiesDescription
                          : ''
                      }
                      {...formik.getFieldProps('necessaryCookiesTitle')}
                    />
                  </div>

                  <Textarea
                    label='Necessary description'
                    error={
                      formik.touched.necessaryCookiesDescription &&
                      formik.errors.necessaryCookiesDescription
                        ? formik.errors.necessaryCookiesDescription
                        : ''
                    }
                    {...formik.getFieldProps('necessaryCookiesDescription')}
                  />
                </Card.Body>
              </Card>

              <Card className='border-0 bg-gray-200' style={{marginBottom: '16px'}}>
                <Card.Body style={{padding: '20px'}}>
                  <div className={`d-flex justify-content-between align-items-center ${formik.values.performanceCookiesEnabled && 'mb-3'}`}>
                    <div className='fw-bold'>Performance Cookies</div>
                    <Form.Check
                      type='switch'
                      id='performance-switch'
                      {...formik.getFieldProps('performanceCookiesEnabled')}
                      checked={formik.values.performanceCookiesEnabled}
                      className='custom-switch-color'
                      style={{
                        transform: 'scale(0.8)',
                        transformOrigin: 'right center',
                      }}
                    />
                  </div>
                  {formik.values.performanceCookiesEnabled && (
                    <>
                  <div style={{marginBottom: '16px'}}>
                    <Input
                      type='text'
                      placeholder='Performance'
                      autoComplete='off'
                      error={
                        formik.touched.performanceCookiesTitle &&
                        formik.errors.performanceCookiesTitle
                          ? formik.errors.performanceCookiesTitle
                          : ''
                      }
                      {...formik.getFieldProps('performanceCookiesTitle')}
                    />
                  </div>

                  <Textarea
                    label='Performance description'
                    error={
                      formik.touched.performanceCookiesDescription &&
                      formik.errors.performanceCookiesDescription
                        ? formik.errors.performanceCookiesDescription
                        : ''
                    }
                    {...formik.getFieldProps('performanceCookiesDescription')}
                  />
                   </>
                  )}
                </Card.Body>
              </Card>

              <Card className='border-0 bg-gray-200' style={{marginBottom: '16px'}}>
                <Card.Body style={{padding: '20px'}}>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <div className='fw-bold'>Targeting Cookies</div>
                    <Form.Check
                      type='switch'
                      id='targeting-switch'
                      {...formik.getFieldProps('targetingCookiesEnabled')}
                      checked={formik.values.targetingCookiesEnabled}
                      className='custom-switch-color'
                      style={{
                        transform: 'scale(0.8)',
                        transformOrigin: 'right center',
                      }}
                    />
                  </div>
                  {formik.values.targetingCookiesEnabled && (
                    <>
                  <div style={{marginBottom: '16px'}}>
                    <Input
                      type='text'
                      placeholder='Targeting'
                      autoComplete='off'
                      error={
                        formik.touched.targetingCookiesTitle && formik.errors.targetingCookiesTitle
                          ? formik.errors.targetingCookiesTitle
                          : ''
                      }
                      {...formik.getFieldProps('targetingCookiesTitle')}
                    />
                  </div>

                  <Textarea
                    label='Targeting description'
                    error={
                      formik.touched.targetingCookiesDescription &&
                      formik.errors.targetingCookiesDescription
                        ? formik.errors.targetingCookiesDescription
                        : ''
                    }
                    {...formik.getFieldProps('targetingCookiesDescription')}
                  />
                  </>
                  )}
                </Card.Body>
              </Card>

              <Card className='border-0 bg-gray-200' style={{marginBottom: '16px'}}>
                <Card.Body style={{padding: '20px'}}>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <div className='fw-bold'>Functional Cookies</div>
                    <Form.Check
                      type='switch'
                      id='functional-switch'
                      {...formik.getFieldProps('functionalCookiesEnabled')}
                      checked={formik.values.functionalCookiesEnabled}
                      className='custom-switch-color'
                      style={{
                        transform: 'scale(0.8)',
                        transformOrigin: 'right center',
                      }}
                    />
                  </div>
                  {formik.values.functionalCookiesEnabled && (
                    <>
                  <div style={{marginBottom: '16px'}}>
                    <Input
                      type='text'
                      placeholder='Functional'
                      autoComplete='off'
                      error={
                        formik.touched.functionalCookiesTitle &&
                        formik.errors.functionalCookiesTitle
                          ? formik.errors.functionalCookiesTitle
                          : ''
                      }
                      {...formik.getFieldProps('functionalCookiesTitle')}
                    />
                  </div>

                  <Textarea
                    label='Functional description'
                    error={
                      formik.touched.functionalCookiesDescription &&
                      formik.errors.functionalCookiesDescription
                        ? formik.errors.functionalCookiesDescription
                        : ''
                    }
                    {...formik.getFieldProps('functionalCookiesDescription')}
                  />
                  </>
                  )}
                </Card.Body>
              </Card>
            </Form.Group>

            <Form.Group style={{marginBottom: '16px'}}>
              <Form.Label style={{marginBottom: '16px'}}>Button Label</Form.Label>
              <Row>
                <Col xs={4} className='d-flex align-items-center '>
                  <div className='fw-semibold text-gray-900'>Save Preferences</div>
                </Col>
                <Col xs={8}>
                  <Input
                    type='text'
                    defaultValue='Save Preferences'
                    {...formik.getFieldProps('savePreferencesLabel')}
                    error={
                      formik.touched.savePreferencesLabel && formik.errors.savePreferencesLabel
                        ? formik.errors.savePreferencesLabel
                        : ''
                    }
                  />
                </Col>
              </Row>
            </Form.Group>
          </div>
        </div>
        <DrawerFooter
          isConnected={false}
          isLoading={isLoading}
          onConnect={handleConnectSubmit}
          onDisconnect={handleClose}
          type='cookie'
          //   isDisable={!validateForm()}
        />
      </Drawer>
      <ToastContainer />
    </>
  )
}

export default CookieCustomizationDrawer